import React from 'react';
import { Box, Paper, Typography, Container, Divider, Link } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, color: '#1A73E8' }}>
      {title}
    </Typography>
    <Box sx={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.7 }}>
      {children}
    </Box>
  </Box>
);

const PrivacyPolicyPage = () => {
  const lastUpdated = 'April 21, 2026';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        <Paper sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 5, pb: 3, borderBottom: '2px solid #E2E8F0' }}>
            <Typography variant="h3" fontWeight={800} sx={{ color: '#1E3A8A', mb: 1 }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gbl Tree Abroad Education
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
              Last updated: {lastUpdated}
            </Typography>
          </Box>

          {/* Intro */}
          <Section title="About Gbl Tree">
            <Typography paragraph>
              <strong>Gbl Tree</strong> is an education loan consultancy that helps students get their education
              loans approved easily from banks. We analyze all documents of the student and guide them on
              the best-fit bank to apply to for the highest chance of loan approval.
            </Typography>
            <Typography paragraph>
              This Privacy Policy explains how we collect, use, and protect your personal information when
              you use our mobile application, student portal, and related services.
            </Typography>
          </Section>

          <Divider sx={{ my: 3 }} />

          <Section title="Information We Collect">
            <Typography paragraph>
              To help you with your education loan application, we collect the following information:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li><strong>Personal details:</strong> Name, email address, phone number, date of birth.</li>
              <li><strong>Academic information:</strong> Course, university, country, intake month, academic scores.</li>
              <li><strong>Loan information:</strong> Requested loan amount, interested banks, previous loan history.</li>
              <li><strong>Identity documents:</strong> Aadhar (front/back), PAN card, Passport, Photo, academic memos, bank statements, salary slips, ITR, CIBIL reports, co-applicant KYC documents, and other documents required by banks for loan processing.</li>
              <li><strong>Device information:</strong> Device type, operating system, app version (for crash diagnostics).</li>
              <li><strong>Firebase authentication data:</strong> Firebase user ID, email verification status.</li>
            </Box>
          </Section>

          <Section title="How We Use Your Information">
            <Typography paragraph>
              We use your information strictly for the following purposes:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>To process your education loan application.</li>
              <li>To analyze your documents and recommend the best-fit bank.</li>
              <li>To communicate with you about your loan application status.</li>
              <li>To share your documents with banks where you have consented to apply.</li>
              <li>To provide customer support through our chat feature.</li>
              <li>To improve our services and fix issues in the app.</li>
            </Box>
            <Typography paragraph sx={{ mt: 2 }}>
              <strong>We do NOT sell, rent, or trade your personal data to any third party.</strong>
            </Typography>
          </Section>

          <Section title="Who Has Access to Your Data">
            <Box component="ul" sx={{ pl: 3 }}>
              <li><strong>Gbl Tree staff</strong> (Admin, Managers, Associates, assigned Banker partners) — only those handling your loan application.</li>
              <li><strong>Partner banks</strong> — only the bank where you choose to apply receives your loan application documents.</li>
              <li><strong>Firebase (Google)</strong> — for authentication and secure user identity.</li>
              <li><strong>Google Cloud Storage</strong> — for secure document storage (encrypted in transit).</li>
            </Box>
          </Section>

          <Section title="Data Security">
            <Typography paragraph>
              We take the security of your personal information seriously:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li>All data is transmitted over <strong>encrypted HTTPS</strong> connections.</li>
              <li>Documents are stored in <strong>Google Cloud Storage</strong> with access controls.</li>
              <li>Authentication is secured via <strong>Firebase Auth + Google Sign-In</strong>.</li>
              <li>Access to your data is restricted to authorized Gbl Tree personnel only.</li>
              <li>Firebase ID tokens are used to authorize every API request.</li>
            </Box>
          </Section>

          <Section title="Device Permissions (Mobile App)">
            <Typography paragraph>
              Our Android/iOS app requests the following permissions:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li><strong>Camera:</strong> To capture photos of documents for upload. No photos are taken automatically.</li>
              <li><strong>Internet:</strong> To communicate with our secure backend.</li>
            </Box>
            <Typography paragraph sx={{ mt: 2 }}>
              We do <strong>not</strong> access your photo gallery, contacts, location, microphone, or any other
              data on your device.
            </Typography>
          </Section>

          <Section title="Your Rights">
            <Typography paragraph>
              As a user, you have the right to:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <li><strong>Access</strong> the personal data we hold about you.</li>
              <li><strong>Correct</strong> any inaccurate information in your profile.</li>
              <li><strong>Delete your account</strong> at any time from within the app (Profile → Delete Account).</li>
              <li><strong>Withdraw consent</strong> for data processing by contacting us.</li>
              <li><strong>Request a copy</strong> of your data by contacting our support team.</li>
            </Box>
          </Section>

          <Section title="Data Retention">
            <Typography paragraph>
              We retain your information only as long as necessary to provide our services or as required by
              applicable laws and banking regulations. When you delete your account, your data is marked for
              removal. Some loan application data may be retained for compliance with banking and
              regulatory requirements even after account deletion.
            </Typography>
          </Section>

          <Section title="Children's Privacy">
            <Typography paragraph>
              Our services are intended for users aged 17 and above who are applying for education loans.
              We do not knowingly collect personal information from children under 13.
            </Typography>
          </Section>

          <Section title="Changes to This Policy">
            <Typography paragraph>
              We may update this Privacy Policy from time to time. When we do, we will update the
              "Last updated" date at the top of this page. Continued use of our services after changes
              constitutes acceptance of the updated policy.
            </Typography>
          </Section>

          <Section title="Contact Us">
            <Typography paragraph>
              If you have any questions, concerns, or requests regarding this Privacy Policy or your data,
              please contact us:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, mt: 2 }}>
              <EmailIcon sx={{ color: '#1A73E8' }} />
              <Link href="mailto:support@gbltree.com" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                support@gbltree.com
              </Link>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LanguageIcon sx={{ color: '#1A73E8' }} />
              <Link href="https://www.gbltree.com" target="_blank" rel="noopener" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                www.gbltree.com
              </Link>
            </Box>
          </Section>

          <Divider sx={{ my: 4 }} />

          <Typography variant="caption" color="text.disabled" align="center" sx={{ display: 'block' }}>
            © {new Date().getFullYear()} Gbl Tree Abroad Education. All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;
